<template>
  <div v-if="messages.length" class="form-messages-overlay" data-testid="form-messages-overlay">
    <div
      v-for="message in messages"
      :key="message.id"
      class="form-messages-overlay__balloon"
      :class="`form-messages-overlay__balloon--${message.type}`"
      role="status"
    >
      <pre class="form-messages-overlay__text">{{ message.text }}</pre>
      <div class="form-messages-overlay__actions">
        <button
          v-for="action in message.actions"
          :key="action"
          type="button"
          class="form-messages-overlay__action"
          :class="`form-messages-overlay__action--${action}`"
          :disabled="isMessageBusy(message.id)"
          @click.stop="onChoose(message, action)"
        >
          {{ formMessageActionLabel(action) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'
import { usePageData } from '@/common/composables/usePageData'
import {
  formMessageActionLabel,
  type FormMessageActionType,
  type FormSoftMessage,
} from '@/services/form-events/formSoftEvent'
import { useFormSoftMessages } from '@/services/form-events/formMessagesStore'
import { executeFormSoftMessageAction } from '@/services/form-events/formEventRouter'

const { pageDataSlot } = usePageData()
const messages = useFormSoftMessages(() => pageDataSlot.value)

const busyMessageIds = ref(new Set<string>())

function isMessageBusy(messageId: string): boolean {
  return busyMessageIds.value.has(messageId)
}

const countdownTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function clearCountdown(messageId: string): void {
  const timeout = countdownTimeouts.get(messageId)
  if (timeout != null) {
    clearTimeout(timeout)
    countdownTimeouts.delete(messageId)
  }
}

function startCountdown(message: FormSoftMessage): void {
  if (!message.autoCountdownMs || !message.autoCountdownAction) return
  const id = message.id
  if (countdownTimeouts.has(id)) return

  const timeout = setTimeout(() => {
    countdownTimeouts.delete(id)
    void triggerAutoAction(message)
  }, message.autoCountdownMs)

  countdownTimeouts.set(id, timeout)
}

async function triggerAutoAction(message: FormSoftMessage): Promise<void> {
  if (!message.autoCountdownAction) return
  if (busyMessageIds.value.has(message.id)) return

  const next = new Set(busyMessageIds.value)
  next.add(message.id)
  busyMessageIds.value = next

  try {
    await executeFormSoftMessageAction(message, message.autoCountdownAction)
  } finally {
    if (busyMessageIds.value.has(message.id)) {
      const cleared = new Set(busyMessageIds.value)
      cleared.delete(message.id)
      busyMessageIds.value = cleared
    }
  }
}

watch(
  messages,
  (newMessages, oldMessages) => {
    const oldIds = new Set((oldMessages ?? []).map((m) => m.id))
    const newIds = new Set(newMessages.map((m) => m.id))

    for (const msg of newMessages) {
      if (!oldIds.has(msg.id) && msg.autoCountdownMs) {
        startCountdown(msg)
      }
    }

    for (const msg of oldMessages ?? []) {
      if (!newIds.has(msg.id)) {
        clearCountdown(msg.id)
      }
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  for (const timeout of countdownTimeouts.values()) {
    clearTimeout(timeout)
  }
  countdownTimeouts.clear()
})

async function onChoose(message: FormSoftMessage, action: FormMessageActionType): Promise<void> {
  if (busyMessageIds.value.has(message.id)) return

  clearCountdown(message.id)

  const next = new Set(busyMessageIds.value)
  next.add(message.id)
  busyMessageIds.value = next

  try {
    await executeFormSoftMessageAction(message, action)
  } finally {
    if (busyMessageIds.value.has(message.id)) {
      const cleared = new Set(busyMessageIds.value)
      cleared.delete(message.id)
      busyMessageIds.value = cleared
    }
  }
}
</script>

<style scoped lang="scss">
.form-messages-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(320px, calc(100% - 16px));
  pointer-events: none;
}

.form-messages-overlay__balloon {
  pointer-events: auto;
  border: 0;
  border-radius: 10px;
  padding: 14px;
  text-align: left;
  box-shadow: 0 4px 12px rgb(0 0 0 / 28%);
  font: inherit;
}

.form-messages-overlay__balloon--alert {
  background: rgb(255, 220, 50);
  color: #111;
}

.form-messages-overlay__balloon--error {
  background: rgb(235, 86, 87);
  color: #fff;
}

.form-messages-overlay__balloon--info {
  background: rgb(65, 65, 65);
  color: #fff;
}

.form-messages-overlay__text {
  margin: 0 0 10px;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.35;
}

.form-messages-overlay__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-messages-overlay__action {
  width: 100%;
  border: 1px solid rgb(0 0 0 / 25%);
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  background: rgb(255 255 255 / 85%);
  color: #111;
  font: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  text-align: center;
}

.form-messages-overlay__balloon--error .form-messages-overlay__action,
.form-messages-overlay__balloon--info .form-messages-overlay__action {
  background: rgb(255 255 255 / 92%);
}

.form-messages-overlay__action--close_form {
  border-color: rgb(160 40 40 / 45%);
}

.form-messages-overlay__action:hover:not(:disabled) {
  filter: brightness(0.97);
}

.form-messages-overlay__action:active:not(:disabled) {
  filter: brightness(0.93);
}

.form-messages-overlay__action:disabled {
  opacity: 0.55;
  cursor: default;
}
</style>
